import { motion } from 'framer-motion';
import { 
  Home as HomeIcon, Building2, Truck, HardHat, Brush, Warehouse, Sparkles,
  CheckCircle, ChevronRight, Phone
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { WaveSeparator } from '../components/WaveSeparator';
import { Navbar } from '../components/Navbar';
import { BubbleBackground } from '../components/BubbleBackground';
import { useBubbleEffect } from '../components/BubbleClick';
import { Link } from 'react-router-dom';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const services = [
  { 
    icon: HomeIcon, 
    title: 'Routine Cleaning', 
    description: 'Keep your home consistently clean with our regular residential cleaning services in Overland Park and Kansas City.',
    image: 'https://customer-assets.emergentagent.com/job_family-cleaning-kc/artifacts/yclah6um_5748846733002107081.jpeg',
    details: [
      'Dusting all surfaces and furniture',
      'Vacuuming carpets and mopping floors',
      'Kitchen and bathroom sanitization',
      'Trash removal and bed making',
      'Window sill and baseboard cleaning'
    ]
  },
  { 
    icon: Sparkles, 
    title: 'Deep Cleaning', 
    description: 'Thorough top-to-bottom cleaning service in Kansas City for when your home needs extra attention.',
    image: 'https://customer-assets.emergentagent.com/job_family-cleaning-kc/artifacts/valxabkm_7421727686695433658.jpeg',
    details: [
      'Inside appliances (oven, microwave, fridge)',
      'Behind and under furniture',
      'Light fixtures and ceiling fans',
      'Baseboards and door frames',
      'Grout and tile scrubbing'
    ]
  },
  { 
    icon: Truck, 
    title: 'Move-In Cleaning', 
    description: 'Start fresh in your new Olathe or Lenexa home with comprehensive move-in cleaning services.',
    image: 'https://customer-assets.emergentagent.com/job_family-cleaning-kc/artifacts/bvt2261f_3337144130066025759.jpeg',
    details: [
      'Complete sanitization of all rooms',
      'Inside cabinets and closets',
      'Appliance deep cleaning',
      'Window and track cleaning',
      'Garage sweeping available'
    ]
  },
  { 
    icon: Brush, 
    title: 'Move-Out Cleaning', 
    description: 'Leave your property spotless and help secure your deposit with our detailed move-out service.',
    image: 'https://customer-assets.emergentagent.com/job_family-cleaning-kc/artifacts/e5z3bzr1_6862006522616167584.jpeg',
    details: [
      'Full kitchen restoration',
      'Bathroom deep sanitization',
      'Carpet vacuuming and spot treatment',
      'Inside cabinets and closets',
      'Final walkthrough ready'
    ]
  },
  { 
    icon: Building2, 
    title: 'Kitchen Cleaning', 
    description: 'Sparkling clean kitchens are our specialty. We make your kitchen shine from top to bottom.',
    image: 'https://customer-assets.emergentagent.com/job_family-cleaning-kc/artifacts/sh4kjh5v_7607940655992651147.jpeg',
    details: [
      'Countertop and backsplash cleaning',
      'Inside and outside of appliances',
      'Cabinet fronts and handles',
      'Sink and faucet polishing',
      'Floor mopping and grout cleaning'
    ]
  },
  { 
    icon: Warehouse, 
    title: 'Bathroom Cleaning', 
    description: 'Deep sanitization and scrubbing to make your bathrooms fresh and spotless.',
    image: 'https://customer-assets.emergentagent.com/job_family-cleaning-kc/artifacts/3opuybmp_849670101801722301.jpeg',
    details: [
      'Toilet deep cleaning and sanitization',
      'Shower and tub scrubbing',
      'Vanity and mirror cleaning',
      'Tile and grout restoration',
      'Floor mopping and disinfecting'
    ]
  },
  { 
    icon: HardHat, 
    title: 'New Construction Cleaning', 
    description: 'Post-construction cleanup for builders and contractors throughout Kansas City.',
    image: 'https://customer-assets.emergentagent.com/job_family-cleaning-kc/artifacts/g6sq648e_5622193362639120542.jpeg',
    details: [
      'Construction debris removal',
      'Dust and particle elimination',
      'Window and glass cleaning',
      'Fixture and hardware polishing',
      'Final inspection ready'
    ]
  },
];

export const ServicesPage = () => {
  const { triggerBubbles, BubbleEffects } = useBubbleEffect();

  const handleOpenQuote = (e) => {
    triggerBubbles(e);
    setTimeout(() => {
      window.location.href = '/#contact';
    }, 300);
  };

  return (
    <div className="min-h-screen bg-white relative">
      <BubbleBackground />
      <BubbleEffects />
      <Navbar onOpenQuote={handleOpenQuote} />

      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-sky-50 to-pink-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial="hidden" animate="visible" variants={fadeInUp}>
            <h1 className="text-4xl md:text-5xl font-outfit font-bold text-slate-800 mb-4">
              Our <span className="text-sky-600">Cleaning</span> Services
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Professional residential and commercial cleaning services in Kansas City, 
              Overland Park, Lenexa, Olathe, and Leawood.
            </p>
          </motion.div>
        </div>
        <WaveSeparator fillColor="#ffffff" />
      </section>

      {/* Services Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-20">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 items-center`}
                data-testid={`service-detail-${index}`}
              >
                {/* Image Side */}
                <div className="lg:w-1/2">
                  <div className="relative rounded-2xl overflow-hidden shadow-xl group">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-64 md:h-80 object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute top-4 left-4">
                      <div className="w-14 h-14 rounded-xl bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-lg">
                        <service.icon className="w-7 h-7 text-sky-600" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Details Side */}
                <div className="lg:w-1/2">
                  <h2 className="text-2xl md:text-3xl font-outfit font-bold text-slate-800 mb-4">{service.title}</h2>
                  <p className="text-slate-600 text-lg mb-6">{service.description}</p>
                  <h3 className="font-outfit font-semibold text-slate-800 mb-4">What's Included:</h3>
                  <ul className="space-y-3">
                    {service.details.map((detail, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-600">{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-sky-500 to-pink-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-outfit font-bold text-white mb-4">
            Ready for a Cleaner Space?
          </h2>
          <p className="text-white/90 text-lg mb-8">
            Get your free, no-obligation quote today. We'll respond within 24 hours.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={handleOpenQuote}
              className="rounded-full bg-white text-sky-600 hover:bg-slate-100 px-8 py-6 text-lg font-medium"
              data-testid="services-cta-quote"
            >
              Get Free Quote
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
            <a href="tel:9132309208">
              <Button
                variant="outline"
                className="rounded-full border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg font-medium"
                data-testid="services-cta-call"
              >
                <Phone className="w-5 h-5 mr-2" />
                (913) 230-9208
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-slate-400">
            &copy; {new Date().getFullYear()} Your Family House Cleaning LLC. All rights reserved.
          </p>
          <Link to="/" className="text-sky-400 hover:text-sky-300 mt-2 inline-block">
            ← Back to Home
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default ServicesPage;
